"use client";

import { useEffect, useState } from "react";
import { onSnapshot, orderBy, query, where } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import {
  choreProposalSchema,
  type ChoreProposal,
  type ProposalStatus,
} from "@/lib/schemas";

type State = {
  proposals: ChoreProposal[];
  loading: boolean;
  error: string | null;
};

type Opts = {
  status?: ProposalStatus;
  proposedBy?: string;
};

const cache = new Map<string, ChoreProposal[]>();
const cacheKey = (hid: string, opts: Opts) =>
  `${hid}|${opts.status ?? "*"}|${opts.proposedBy ?? "*"}`;

export function useChoreProposals(
  householdId: string | undefined,
  opts: Opts = {},
): State {
  const { status, proposedBy } = opts;
  const key = householdId
    ? cacheKey(householdId, { status, proposedBy })
    : "";
  const cached = key ? cache.get(key) : undefined;
  const [state, setState] = useState<State>({
    proposals: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const constraints = [];
    if (status) constraints.push(where("status", "==", status));
    if (proposedBy) constraints.push(where("proposedBy", "==", proposedBy));
    constraints.push(orderBy("proposedAt", "desc"));
    const q = query(refCol(db, paths.choreProposals(householdId)), ...constraints);
    const k = cacheKey(householdId, { status, proposedBy });
    const unsub = onSnapshot(
      q,
      (snap) => {
        const proposals = snap.docs.flatMap((d) => {
          const parsed = choreProposalSchema.safeParse({
            id: d.id,
            ...d.data(),
          });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(k, proposals);
        setState({ proposals, loading: false, error: null });
      },
      (err) =>
        setState({ proposals: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId, status, proposedBy]);

  return state;
}
